import { Filter, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { downloadUrlFromRemoteOrigin } from "@/lib/utility/downloadFileFunc";
import {
  MrfiktivImageMetaDatViewmodelGen,
  MrfiktivPartnerImageViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { PartnerImageDataAccessLayer } from "@/store/modules/access-layers/partner-image.access-layer";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import partnerImageService from "@/services/mrfiktiv/services/partnerImageService";
import { ImageEntity } from "./imageEntity";
import { ReportImageType } from "./Report/ReportImageType";

@IsFilterable
class PartnerImageBase implements IEntity<MrfiktivPartnerImageViewModelGen> {
  /**
   * The id of the image
   * @example 6389d1ae67c5cecbb2762cfa
   */
  id: string;

  /**
   * The id of the user uploaded the image
   * @example 6389d1ae67c5cecbb2762cfa
   */
  userId?: string;

  /**
   * The id of the partner uploaded the image
   * @example 6389d1ae67c5cecbb2762cfa
   */
  partnerId?: string;

  /**
   * The id of the folder
   * @example abc123
   */
  folderName: string;

  /**
   * The name of the original image
   * @example abc123
   */
  name: string;

  url: string;

  /**
   * The type of the image
   * @example damage
   */
  type: ReportImageType;

  /** Meta data of an image */
  metaData?: MrfiktivImageMetaDatViewmodelGen;

  /**
   * Is true if the picture is used in form, report or case.
   * @example damage
   */
  isUsed: boolean;

  /**
   * Where the image was provided from.
   * @example damage
   */
  contactType:
    | "form"
    | "report"
    | "sign"
    | "chargingStation"
    | "booking"
    | "thg"
    | "case"
    | "ticket"
    | "vehicle"
    | "portal"
    | "other"
    | "thirdParty"
    | "anonymization"
    | "message";

  /** The timestamp */
  timestamp: ITimestamp;

  _file?: File;

  constructor(image?: Partial<MrfiktivPartnerImageViewModelGen> & { file?: File }) {
    this.id = image?.id ?? "";
    this.userId = image?.userId;
    this.partnerId = image?.partnerId;
    this.folderName = image?.folderName ?? "";
    this.name = image?.name ?? "";
    this.url = image?.url ?? "";
    this.type = (image?.type as ReportImageType) ?? ReportImageType.overview;
    this.metaData = image?.metaData;
    this.isUsed = image?.isUsed ?? false;
    this.contactType = image?.contactType ?? "other";
    this.timestamp = new Timestamp(image?.timestamp);
    this._file = image?.file;
  }

  map(image?: MrfiktivPartnerImageViewModelGen | ImageEntity) {
    if (!image) return;

    const i = image as MrfiktivPartnerImageViewModelGen;
    this.id = i?.id;
    this.userId = i?.userId;
    this.partnerId = i?.partnerId;
    this.folderName = i?.folderName ?? "";
    this.name = i?.name ?? "";
    this.url = i?.url ?? "";
    this.type = (i?.type as ReportImageType) ?? ReportImageType.overview;
    this.metaData = i?.metaData;
    this.isUsed = i?.isUsed ?? false;
    this.contactType = i?.contactType ?? "other";
    this.timestamp = new Timestamp(i?.timestamp);
  }

  async fetch(): Promise<this> {
    if (!this.id) {
      throw new Error("Can not fetch image. No ID provided");
    }

    if (!this.partnerId) {
      throw new Error("Can not fetch image without partnerId");
    }

    const res = await partnerImageService.getOne(this.partnerId, this.id);

    this.map(res);

    PartnerImageDataAccessLayer.set(this);

    return this;
  }

  async download(): Promise<boolean> {
    return await downloadUrlFromRemoteOrigin(this.url, this.name);
  }

  async create(): Promise<this> {
    if (!this.partnerId) {
      return Promise.reject("partnerId is required to create a partner image");
    }

    if (!this._file) {
      return Promise.reject("No file data available to create partner image");
    }

    const created = await partnerImageService.create(this.partnerId, {
      image: this._file,
      type: this.type
    });

    this.map(created);
    PartnerImageDataAccessLayer.set(this);

    return this;
  }
}

type IPartnerImage = PartnerImageBase;
const PartnerImage = Filter.createForClass(PartnerImageBase);

export { IPartnerImage, PartnerImage };
